import type {
  SiteComplianceNetworkTier,
  SiteComplianceSite,
  SiteComplianceSiteType,
} from './siteCompliance.types';

export const SITE_COMPLIANCE_PAGE_SIZE_OPTIONS = [10, 30, 50];

export const SITE_COMPLIANCE_SITE_TYPE_LABELS: Record<
  SiteComplianceSiteType,
  string
> = {
  office: 'Office',
  office_plus: 'Office +',
  flex: 'Flex',
  datacenter: 'Datacenter',
};

export const SITE_COMPLIANCE_NETWORK_TIER_LABELS: Record<
  Exclude<SiteComplianceNetworkTier, null>,
  string
> = {
  enhanced: 'Enhanced',
  flagship: 'Flagship',
  standard: 'Standard',
};

const SITE_COMPLIANCE_DIVISION_ALIASES: Record<string, string> = {
  'CP&ESG': 'CPESG',
  Corp: 'CORPORATE',
  Health: 'HEALTH',
};

export const normalizeSiteComplianceDivision = (division: string) => {
  const trimmedDivision = division.trim();
  return (
    SITE_COMPLIANCE_DIVISION_ALIASES[trimmedDivision] ??
    trimmedDivision.toUpperCase()
  );
};

export const getSiteComplianceDivisionLabels = (
  site: SiteComplianceSite,
) => {
  return site.primaryDivisions.map(normalizeSiteComplianceDivision);
};

export const getSiteCompliancePrimaryDivisionLabel = (
  site: SiteComplianceSite,
) => {
  if (site.primaryDivision) {
    return normalizeSiteComplianceDivision(site.primaryDivision);
  }

  return getSiteComplianceDivisionLabels(site)[0] ?? 'Unknown';
};

export const getSiteComplianceDivisionSummary = (
  site: SiteComplianceSite,
) => {
  const divisionLabels = getSiteComplianceDivisionLabels(site);

  return divisionLabels.length ? divisionLabels.join(', ') : 'Unknown';
};

export const getSiteComplianceDivisionSlug = (
  site: SiteComplianceSite,
) => {
  const divisionLabels = getSiteComplianceDivisionLabels(site);

  return divisionLabels.length ? divisionLabels.join('-') : 'Shared';
};

export const SITE_COMPLIANCE_UNKNOWN_DIVISION = 'Unknown';

export const buildSiteComplianceDivisionOptions = (
  sites: SiteComplianceSite[],
) => {
  const named = new Set<string>();
  let hasUnknown = false;
  for (const site of sites) {
    const labels = getSiteComplianceDivisionLabels(site);
    if (labels.length === 0) {
      hasUnknown = true;
    } else {
      labels.forEach((label) => named.add(label));
    }
  }
  const sorted = Array.from(named).sort((left, right) =>
    left.localeCompare(right),
  );
  if (hasUnknown) {
    sorted.push(SITE_COMPLIANCE_UNKNOWN_DIVISION);
  }
  return sorted;
};

export const buildSiteComplianceBusinessUnitOptions = (
  sites: SiteComplianceSite[],
) => {
  return Array.from(
    new Set(sites.flatMap((site) => site.businessUnits)),
  ).sort((left, right) => left.localeCompare(right));
};

export const buildSiteComplianceRegionOptions = (
  sites: SiteComplianceSite[],
) => {
  return Array.from(new Set(sites.map((site) => site.region).filter(Boolean))).sort(
    (left, right) => left.localeCompare(right),
  );
};

export const filterSiteComplianceSites = ({
  businessUnit,
  divisions,
  enhancedOnly,
  flagshipOnly,
  region,
  siteIds,
  siteType,
  sites,
}: {
  businessUnit: string;
  divisions: string[];
  enhancedOnly: boolean;
  flagshipOnly: boolean;
  region: string;
  siteIds: string[];
  siteType: SiteComplianceSiteType | 'ALL';
  sites: SiteComplianceSite[];
}) => {
  const selectedDivisions = divisions.map(normalizeSiteComplianceDivision);

  return sites.filter((site) => {
    const siteDivisions = getSiteComplianceDivisionLabels(site);
    const matchesDivision =
      selectedDivisions.length === 0 ||
      selectedDivisions.some((division) =>
        division === SITE_COMPLIANCE_UNKNOWN_DIVISION
          ? siteDivisions.length === 0
          : siteDivisions.includes(division),
      );
    const matchesSite = !siteIds.length || siteIds.includes(site.siteId);
    const matchesType = siteType === 'ALL' || site.siteType === siteType;
    const matchesBusinessUnit =
      !businessUnit || site.businessUnits.includes(businessUnit);
    const matchesRegion = !region || site.region === region;
    const matchesTier =
      flagshipOnly && enhancedOnly
        ? site.isFlagship || site.isEnhanced
        : flagshipOnly
          ? site.isFlagship
          : enhancedOnly
            ? site.isEnhanced
            : true;

    return (
      matchesDivision &&
      matchesSite &&
      matchesType &&
      matchesBusinessUnit &&
      matchesRegion &&
      matchesTier
    );
  });
};

export const paginateSiteComplianceSites = ({
  limit,
  page,
  sites,
}: {
  limit: number;
  page: number;
  sites: SiteComplianceSite[];
}) => {
  const startIndex = (page - 1) * limit;
  return sites.slice(startIndex, startIndex + limit);
};
